import React, { useCallback, useContext, useMemo } from 'react';

import { AuthContext, type AuthContextValue } from '~/auth';
import dayjs from '~/timeInit';

import { Box, Container, Paper, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';

import {
  ROLE_ADMIN_USER,
  ROLE_EMPLOYEE_USER,
  ROLE_EXPERIENCES_COORDINATOR,
  ROLE_ICS_STORE_TEAM,
  ROLE_TOUR_COORDINATOR,
} from '~/consts/roles';

import { useGetLeAgentMapping } from '~/hooks/apis/reporting/useGetLeAgentMapping';
import useAppNavigation from '~/hooks/useAppNavigation';

import { type LeAgentMappingReportRow } from '~/services/ReportingService';

import { TryBetaButton } from './Common/TryBetaButton';
import UserSearchWidget, { SearchResult } from './Common/UserSearchWidget';
import RecentlyViewedProducts from './Products/RecentlyViewedProducts';
import RecentlyViewedPurchases from './Purchases/RecentlyViewedPurchases';
import RecentUsers from './Users/RecentUsers';
import { withTenant } from './hoc';

const SALES_DEPARTMENT_KEYWORD = 'sales';
const SALES_PERFORMANCE_DASHBOARD_PATH = '/sales-performance-dashboard';

type SalesPerformanceDashboardLinkUser = {
  id_member?: string | null;
  memberId?: string | null;
};

export function canShowSalesPerformanceDashboardLink(
  user: SalesPerformanceDashboardLinkUser | null | undefined,
  agentMappingRows: Array<Pick<LeAgentMappingReportRow, 'department' | 'user_id'>>,
): boolean {
  const currentUserId = user?.memberId || user?.id_member;

  if (!currentUserId) {
    return false;
  }

  return agentMappingRows.some(
    (row) => row.user_id === currentUserId && row.department.toLowerCase().includes(SALES_DEPARTMENT_KEYWORD),
  );
}

function IndexPage() {
  const { user } = useContext<AuthContextValue>(AuthContext);
  const { push } = useAppNavigation();
  const handleWidgetChange = useCallback(() => {}, []);
  const currentUserId = user?.memberId || user?.id_member || null;
  const agentMappingQuery = useGetLeAgentMapping({}, { enabled: Boolean(currentUserId) });

  const canSearchUsers = useMemo(() => {
    if (!user?.roles) {
      return false;
    }

    const allowedRoles = [
      ROLE_ADMIN_USER,
      ROLE_EMPLOYEE_USER,
      ROLE_EXPERIENCES_COORDINATOR,
      ROLE_ICS_STORE_TEAM,
      ROLE_TOUR_COORDINATOR,
    ];

    return allowedRoles.some((role) => user.roles.includes(role));
  }, [user]);

  const handleSearchSelect = useCallback(
    (result: SearchResult | null) => {
      if (!result) {
        return;
      }

      let pathname: string;
      if (result.entityType === 'order') {
        pathname = `/purchases/${result.value}`;
      } else if (result.entityType === 'offer') {
        pathname = result.targetPath ?? `/offers/${result.value}`;
      } else {
        pathname = `/users/${result.value}`;
      }
      push({ pathname });
    },
    [push],
  );

  const showLastLogin = useMemo(() => {
    if (user) {
      const lastLogin = user.lastLogin;
      if (lastLogin?.login_date) {
        const lastLoginDate = new Date(lastLogin.login_date);
        return (
          <Box>
            You last logged in {dayjs(lastLoginDate).fromNow()} on {dayjs(lastLoginDate).calendar()}{' '}
          </Box>
        );
      }
    }
  }, [user]);

  const showSalesPerformanceDashboardLink = useMemo(
    () => canShowSalesPerformanceDashboardLink(user, agentMappingQuery.data?.data ?? []),
    [agentMappingQuery.data?.data, user],
  );

  return (
    <Container maxWidth="xl">
      <Paper className="user-panel">
        <Typography fontWeight="bold" variant="h3">
          Welcome {user ? ' ' + user.givenName : null}!
        </Typography>
        {showLastLogin}
        <TryBetaButton />
        {showSalesPerformanceDashboardLink && (
          <Box mt={1}>
            <Box component="a" href={SALES_PERFORMANCE_DASHBOARD_PATH}>
              Sales performance dashboard
            </Box>
          </Box>
        )}
      </Paper>
      {canSearchUsers && (
        <Box mt={4}>
          <UserSearchWidget
            id="fk_customer_id"
            label="Search for a customer, Société account, offer or order"
            formErrors={[]}
            loading={false}
            onChange={handleWidgetChange}
            includeOrders
            includeOffers
            searchAllTenants
            onSelectResult={handleSearchSelect}
          />
        </Box>
      )}
      <Grid container spacing={2} paddingTop={4}>
        <Grid size={{ xs: 12, md: 6 }}>
          <RecentUsers />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <RecentlyViewedPurchases />
          <Box mt={4}>
            <RecentlyViewedProducts />
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
}

export default withTenant(IndexPage);
